import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import { Calendar, type DateRange } from "@loot-os/ui/components/calendar";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@loot-os/ui/components/command";
import { Input } from "@loot-os/ui/components/input";
import { Popover, PopoverContent, PopoverTrigger } from "@loot-os/ui/components/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { CalendarIcon, ChevronsUpDown, X } from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";

import { ErrorAlert } from "../components/ErrorAlert";
import { FilterField } from "../components/FilterField";
import { ACTOR_LABELS, ACTOR_TYPES, type AuditLogSearch, isActorType } from "../lib/auditLog";
import { formatDate, formatShortDate } from "../lib/format";
import { useTRPC } from "../lib/trpc";
import { useDebounced } from "../lib/useDebounced";

const PAGE_SIZE = 50;
const ALL = "all";

// What each select shows in its trigger. Base UI renders the raw value there unless the root
// is handed the labels, and a tenant id or "all" is not something to read.
const ACTOR_ITEMS: Record<string, string> = { [ALL]: "Anyone", ...ACTOR_LABELS };
const RESULT_ITEMS: Record<string, string> = {
  [ALL]: "Any result",
  ok: "Succeeded",
  failed: "Refused or failed",
};

/** `metadata` as the middleware writes it: `{ input, output?, details }`, every key allowlisted. */
interface AuditMetadata {
  input?: unknown;
  output?: unknown;
  details?: { email?: string | null; isPlatformAdmin?: boolean; ip?: string | null };
}

function readMetadata(value: unknown): AuditMetadata {
  return value && typeof value === "object" ? (value as AuditMetadata) : {};
}

/** A date input's value at the start (or, for `to`, the end) of that day, in local time. */
function dayBound(value: string | undefined, edge: "start" | "end"): Date | undefined {
  if (!value) return undefined;
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return undefined;
  if (edge === "end") date.setDate(date.getDate() + 1);
  return date;
}

function ResultBadge({ success }: { success: boolean }) {
  return <Badge variant={success ? "default" : "destructive"}>{success ? "ok" : "refused"}</Badge>;
}

/**
 * The tenant filter as a searchable combobox, the way the box template editor picks catalog
 * items and the payments form picks a country: a platform with many tenants is typed into,
 * not scrolled. Matching is on name and slug; `undefined` is every tenant.
 */
function TenantCombobox({
  id,
  tenants,
  value,
  onChange,
}: {
  id: string;
  tenants: { id: string; name: string; slug: string }[];
  value: string | undefined;
  onChange: (tenantId: string | undefined) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = value ? tenants.find((t) => t.id === value) : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            id={id}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
          />
        }
      >
        <span className="truncate">
          {value === undefined ? "All tenants" : (selected?.name ?? value)}
        </span>
        <ChevronsUpDown className="size-3.5 opacity-60" />
      </PopoverTrigger>
      {/* A fixed width, like the catalog picker: the trigger's column is too narrow for a
          name and a slug side by side, and a slug that wraps reads as three tenants. */}
      <PopoverContent className="w-[22rem] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search tenants…" />
          <CommandList>
            <CommandEmpty>No tenant matches.</CommandEmpty>
            <CommandItem
              value="All tenants"
              onSelect={() => {
                onChange(undefined);
                setOpen(false);
              }}
            >
              All tenants
            </CommandItem>
            {tenants.map((tenant) => (
              <CommandItem
                key={tenant.id}
                value={tenant.name}
                keywords={[tenant.slug, tenant.id]}
                onSelect={() => {
                  onChange(tenant.id);
                  setOpen(false);
                }}
              >
                <span className="truncate">{tenant.name}</span>
                <span className="ml-auto shrink-0 font-mono text-muted-foreground text-xs">
                  {tenant.slug}
                </span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** A calendar day as the URL carries it, `YYYY-MM-DD` in local time. */
function toDayKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** The local midnight of a `YYYY-MM-DD` key, or undefined when it is not one. */
function fromDayKey(value: string | undefined): Date | undefined {
  if (!value) return undefined;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

/**
 * The date-range filter as a two-month calendar in a popover (shadcn's range picker on the
 * shared Calendar). The URL keeps `from` and `to` as day keys, inclusive on both ends; the
 * query turns `to` into the end of that day.
 */
function DateRangePicker({
  id,
  from,
  to,
  onChange,
}: {
  id: string;
  from: string | undefined;
  to: string | undefined;
  onChange: (range: { from: string | undefined; to: string | undefined }) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected: DateRange | undefined = from
    ? { from: fromDayKey(from), to: fromDayKey(to) }
    : undefined;
  const label =
    selected?.from === undefined
      ? "Any time"
      : selected.to
        ? `${formatShortDate(selected.from)} – ${formatShortDate(selected.to)}`
        : `${formatShortDate(selected.from)} – …`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            id={id}
            type="button"
            variant="outline"
            aria-expanded={open}
            className="w-full justify-start px-2.5 font-normal"
          />
        }
      >
        <CalendarIcon data-icon="inline-start" className="text-muted-foreground" />
        <span className={selected ? undefined : "text-muted-foreground"}>{label}</span>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          numberOfMonths={2}
          defaultMonth={selected?.from ?? new Date()}
          selected={selected}
          onSelect={(range) =>
            onChange({
              from: range?.from ? toDayKey(range.from) : undefined,
              to: range?.to ? toDayKey(range.to) : undefined,
            })
          }
        />
        {selected && (
          <div className="flex justify-end border-t px-3 py-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                onChange({ from: undefined, to: undefined });
                setOpen(false);
              }}
            >
              Clear dates
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

/** Every action taken with authority, newest first. Read-only: the table refuses edits. */
export function AuditLogPage() {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const search = useSearch({ from: "/app/audit" });

  // Free-text filters are typed into local state and reach the URL (and the query) settled.
  const [action, setAction] = useState(search.action ?? "");
  const [resource, setResource] = useState(search.resource ?? "");
  const [actor, setActor] = useState(search.actor ?? "");
  const debouncedAction = useDebounced(action);
  const debouncedResource = useDebounced(resource);
  const debouncedActor = useDebounced(actor);
  useEffect(() => setAction(search.action ?? ""), [search.action]);
  useEffect(() => setResource(search.resource ?? ""), [search.resource]);
  useEffect(() => setActor(search.actor ?? ""), [search.actor]);
  useEffect(() => {
    const next = {
      action: debouncedAction.trim() || undefined,
      resource: debouncedResource.trim() || undefined,
      actor: debouncedActor.trim() || undefined,
    };
    if (
      next.action === search.action &&
      next.resource === search.resource &&
      next.actor === search.actor
    ) {
      return;
    }
    void navigate({ to: ".", search: (prev: AuditLogSearch) => ({ ...prev, ...next }) });
  }, [debouncedAction, debouncedResource, debouncedActor, navigate, search]);

  const setSearch = (patch: Partial<AuditLogSearch>) =>
    void navigate({ to: ".", search: (prev: AuditLogSearch) => ({ ...prev, ...patch }) });

  const tenants = useQuery(trpc.platform.tenants.list.queryOptions());
  const tenantById = useMemo(
    () => new Map((tenants.data ?? []).map((t) => [t.id, t] as const)),
    [tenants.data],
  );
  // By name for the combobox; the list itself is newest first.
  const tenantOptions = useMemo(
    () => [...(tenants.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [tenants.data],
  );

  const filter = useMemo(
    () => ({
      tenantId: search.tenant,
      actorType: search.actorType,
      success: search.result === undefined ? undefined : search.result === "ok",
      action: search.action,
      resourceId: search.resource,
      actorId: search.actor,
      from: dayBound(search.from, "start"),
      to: dayBound(search.to, "end"),
    }),
    [search],
  );

  const log = useInfiniteQuery(
    trpc.platform.auditLogs.list.infiniteQueryOptions(
      { ...filter, limit: PAGE_SIZE },
      { getNextPageParam: (last) => last.nextCursor, placeholderData: (previous) => previous },
    ),
  );
  const rows = log.data?.pages.flatMap((page) => page.items) ?? [];
  const [expanded, setExpanded] = useState<string | null>(null);

  const hasFilter = Object.values(search).some((v) => v !== undefined);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Audit log</h1>
        <p className="text-sm text-muted-foreground">
          Every action taken with authority on Loot OS: dashboard changes, sign-ins, and what
          tenants' servers do with their API keys. Newest first. Rows are never edited or removed.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filter</CardTitle>
          <CardDescription>
            Ids match exactly; the action is the procedure or route as recorded.
          </CardDescription>
          {hasFilter && (
            <CardAction>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => void navigate({ to: ".", search: {} })}
              >
                <X />
                Clear filters
              </Button>
            </CardAction>
          )}
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <FilterField id="audit-tenant" label="Tenant">
            <TenantCombobox
              id="audit-tenant"
              tenants={tenantOptions}
              value={search.tenant}
              onChange={(tenant) => setSearch({ tenant })}
            />
          </FilterField>
          <FilterField id="audit-actor-type" label="Who">
            <Select
              items={ACTOR_ITEMS}
              value={search.actorType ?? ALL}
              onValueChange={(value) =>
                setSearch({ actorType: isActorType(value) ? value : undefined })
              }
            >
              <SelectTrigger id="audit-actor-type" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{ACTOR_ITEMS[ALL]}</SelectItem>
                {ACTOR_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {ACTOR_LABELS[type]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
          <FilterField id="audit-result" label="Result">
            <Select
              items={RESULT_ITEMS}
              value={search.result ?? ALL}
              onValueChange={(value) =>
                setSearch({ result: value === "ok" || value === "failed" ? value : undefined })
              }
            >
              <SelectTrigger id="audit-result" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(RESULT_ITEMS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
          <FilterField id="audit-action" label="Action">
            <Input
              id="audit-action"
              placeholder="e.g. users.setLock"
              value={action}
              onChange={(e) => setAction(e.target.value)}
              className="font-mono text-xs"
            />
          </FilterField>
          <FilterField id="audit-resource" label="Resource id">
            <Input
              id="audit-resource"
              placeholder="End user, box or tenant id"
              value={resource}
              onChange={(e) => setResource(e.target.value)}
              className="font-mono text-xs"
            />
          </FilterField>
          <FilterField id="audit-actor" label="Actor id">
            <Input
              id="audit-actor"
              placeholder="User or API key id"
              value={actor}
              onChange={(e) => setActor(e.target.value)}
              className="font-mono text-xs"
            />
          </FilterField>
          <FilterField id="audit-dates" label="Date range" className="sm:col-span-2">
            <DateRangePicker
              id="audit-dates"
              from={search.from}
              to={search.to}
              onChange={(range) => setSearch(range)}
            />
          </FilterField>
        </CardContent>
      </Card>

      {log.error && <ErrorAlert message={log.error.message} />}

      <Card>
        <CardHeader>
          <CardTitle>
            {rows.length} {rows.length === 1 ? "entry" : "entries"}
            {log.hasNextPage ? " loaded" : ""}
          </CardTitle>
          <CardDescription>Click a row for the recorded input, output and details.</CardDescription>
        </CardHeader>
        <CardContent>
          {/* The Table sits directly under CardContent, like every other list here: the shared
              component bleeds it to the card's edges and gives the first and last cells the
              card's own padding only from that position. */}
          <Table className="table-fixed">
            {/* Fixed widths for the columns whose content is one shape (a timestamp, a badge, a
                tenant name); the id-bearing columns share the rest and truncate, so the table
                never grows past the card. The full values are in the expanded row. */}
            <colgroup>
              <col className="w-48" />
              <col />
              <col />
              <col className="w-32" />
              <col />
              <col className="w-24" />
            </colgroup>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Who</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Tenant</TableHead>
                <TableHead>Resource</TableHead>
                <TableHead>Result</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {log.isLoading && (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!log.isLoading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                    {hasFilter
                      ? "Nothing matches these filters."
                      : "Nothing has been recorded yet."}
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row) => {
                const metadata = readMetadata(row.metadata);
                const tenant = row.tenantId ? tenantById.get(row.tenantId) : undefined;
                const isOpen = expanded === row.id;
                return (
                  <Fragment key={row.id}>
                    <TableRow
                      className="cursor-pointer"
                      aria-expanded={isOpen}
                      onClick={() => setExpanded(isOpen ? null : row.id)}
                    >
                      <TableCell className="text-muted-foreground">
                        {formatDate(row.createdAt)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-xs text-muted-foreground">
                            {isActorType(row.actorType)
                              ? ACTOR_LABELS[row.actorType]
                              : row.actorType}
                          </span>
                          <ActorCell
                            actorType={row.actorType}
                            actorId={row.actorId}
                            email={metadata.details?.email ?? null}
                            tenantId={row.tenantId}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-normal break-words">
                        <ActionCell action={row.action} />
                      </TableCell>
                      <TableCell className="truncate">
                        {row.tenantId ? (
                          <Link
                            to="/tenants/$tenantId"
                            params={{ tenantId: row.tenantId }}
                            className="text-primary hover:underline"
                            title={tenant?.name ?? row.tenantId}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {tenant?.name ?? row.tenantId}
                          </Link>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <ResourceCell
                          resourceType={row.resourceType}
                          resourceId={row.resourceId}
                          tenantId={row.tenantId}
                        />
                      </TableCell>
                      <TableCell>
                        <ResultBadge success={row.success} />
                      </TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow className="bg-muted/40 hover:bg-muted/40">
                        <TableCell colSpan={6} className="whitespace-normal">
                          <div className="grid gap-3 py-1 md:grid-cols-3">
                            <MetadataBlock label="Input" value={metadata.input} />
                            <MetadataBlock label="Output" value={metadata.output} />
                            <MetadataBlock label="Details" value={metadata.details} />
                          </div>
                          <p className="mt-2 font-mono text-[11px] text-muted-foreground">
                            row {row.id} · actor {row.actorId}
                            {row.resourceId ? ` · resource ${row.resourceId}` : ""}
                            {row.environmentId ? ` · environment ${row.environmentId}` : ""}
                          </p>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
          {log.hasNextPage && (
            <div className="mt-4 flex justify-center">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={log.isFetchingNextPage}
                onClick={() => void log.fetchNextPage()}
              >
                {log.isFetchingNextPage ? "Loading…" : "Load older entries"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ActorCell({
  actorType,
  actorId,
  email,
  tenantId,
}: {
  actorType: string;
  actorId: string;
  email: string | null;
  tenantId: string | null;
}) {
  if (actorType === "end_user" && tenantId) {
    return (
      <Link
        to="/tenants/$tenantId/users/$userId"
        params={{ tenantId, userId: actorId }}
        className="truncate font-mono text-xs text-primary hover:underline"
        title={actorId}
        onClick={(e) => e.stopPropagation()}
      >
        {actorId}
      </Link>
    );
  }
  if (actorType === "admin" && email) {
    return (
      <span className="truncate text-sm" title={email}>
        {email}
      </span>
    );
  }
  return (
    <span className="truncate font-mono text-xs" title={actorId}>
      {actorId}
    </span>
  );
}

/**
 * The action with a soft break after every `.` and `/` and before every capital, so a long
 * procedure path or route wraps at a segment (`storefront.` / `setShippingDestinations`, then
 * `setShipping` / `Destinations` in a narrower column) rather than mid-word or past the column.
 * `<wbr>` is not copied, unlike a zero-width space, so a pasted action still filters.
 */
function ActionCell({ action }: { action: string }) {
  // Keyed by where each segment starts in the string, which is unique where the text is not
  // (`/v1/` repeats).
  const segments: { offset: number; text: string }[] = [];
  for (const text of action.split(/(?<=[./])|(?=[A-Z])/)) {
    const previous = segments.at(-1);
    segments.push({ offset: previous ? previous.offset + previous.text.length : 0, text });
  }
  return (
    <span className="font-mono text-xs">
      {segments.map((segment, i) => (
        <Fragment key={segment.offset}>
          {segment.text}
          {i < segments.length - 1 && <wbr />}
        </Fragment>
      ))}
    </span>
  );
}

function ResourceCell({
  resourceType,
  resourceId,
  tenantId,
}: {
  resourceType: string;
  resourceId: string | null;
  tenantId: string | null;
}) {
  const label = resourceType.toLowerCase().replaceAll("_", " ");
  if (!resourceId) {
    return <span className="block truncate text-xs text-muted-foreground">{label}</span>;
  }
  const link =
    resourceType === "END_USER" && tenantId ? (
      <Link
        to="/tenants/$tenantId/users/$userId"
        params={{ tenantId, userId: resourceId }}
        className="truncate font-mono text-xs text-primary hover:underline"
        title={resourceId}
        onClick={(e) => e.stopPropagation()}
      >
        {resourceId}
      </Link>
    ) : resourceType === "TENANT" ? (
      <Link
        to="/tenants/$tenantId"
        params={{ tenantId: resourceId }}
        className="truncate font-mono text-xs text-primary hover:underline"
        title={resourceId}
        onClick={(e) => e.stopPropagation()}
      >
        {resourceId}
      </Link>
    ) : (
      <span className="truncate font-mono text-xs" title={resourceId}>
        {resourceId}
      </span>
    );
  return (
    <div className="flex flex-col">
      <span className="truncate text-xs text-muted-foreground">{label}</span>
      {link}
    </div>
  );
}

function MetadataBlock({ label, value }: { label: string; value: unknown }) {
  const empty =
    value === undefined ||
    value === null ||
    (typeof value === "object" && Object.keys(value as object).length === 0);
  return (
    <div className="min-w-0">
      <p className="mb-1 text-xs font-medium text-muted-foreground">{label}</p>
      {empty ? (
        <p className="text-xs text-muted-foreground">—</p>
      ) : (
        <pre className="max-h-64 overflow-auto rounded-md border bg-background p-2 font-mono text-[11px] leading-relaxed">
          {JSON.stringify(value, null, 2)}
        </pre>
      )}
    </div>
  );
}
